import * as z from "zod";

const ACCEPTED_IMAGE_TYPES = [
  "image/jpg",
  "image/png",
  "image/jpeg",
  undefined,
  null,
];

export const TenantFormRule = z.object({
  owner: z.string().optional(),
  businessName: z.string().optional(),
  businessType: z.string().optional(),
  description: z.string().optional(),
  avatar: z.string().optional(),
  email: z.string().optional(),
  mobileNumber: z.string().optional(),
  phoneNumber: z.string().optional(),
  website: z.string().optional(),
  membershipDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  rewardsType: z.string().optional(),
  slug: z.string().optional(),
  postRegistrationLink: z.string().optional(),
  postRegistrationMessage: z.string().optional(),
  allowUnderEighteen: z.boolean().optional(),
  allowDecimalPoints: z.boolean().optional(),
  featureQuota: z.boolean().optional(),
  featureSplashScreen: z.boolean().optional(),
  featureCoupon: z.boolean().optional(),
  featureReferral: z.boolean().optional(),
  featureBirthdayEmail: z.boolean().optional(),
  smtpAppPassword: z.string().optional(),
  smtpMailUser: z.string().optional(),
  smtpMailFrom: z.string().optional(),
  smtpMailContent: z.string().optional(),
  termsAndConditions: z.string().optional(),
  privacyPolicy: z.string().optional(),
});
export const RoleFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});
export const PersonFormRule = z.object({
  tenant: z.string().optional(),
  role: z.string().optional(),
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  username: z
    .string()
    .optional()
    .refine((val) => !val || !/\s/.test(val), {
      message: "Username must not contain spaces",
    }),
  email: z.string().optional(),
  password: z.string().optional(),
  isVerified: z.boolean().optional(),
  avatar: z.string().optional(),
  activationCode: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.coerce.date().optional().nullish().or(z.string().max(0)),
  isBlocked: z.boolean().optional(),
  mobileNumber: z.string().optional(),
  fatherName: z.string().optional(),
  motherName: z.string().optional(),
  emergencyContact_1Number: z.string().optional(),
  emergencyContact_1Name: z.string().optional(),
  emergencyContact_1Relationship: z.string().optional(),
  emergencyContact_2Number: z.string().optional(),
  emergencyContact_2Name: z.string().optional(),
  emergencyContact_2Relationship: z.string().optional(),
  sss: z.string().optional(),
  philhealth: z.string().optional(),
  pagibig: z.string().optional(),
  tin: z.string().optional(),
  medicalCondition: z.string().optional(),
  bloodType: z.string().optional(),
  elementary: z.string().optional(),
  highSchool: z.string().optional(),
  college: z.string().optional(),
  postCollege: z.string().optional(),
  undergraduate: z.string().optional(),
  curriculumVitae: z.string().optional(),
  supportingDoc: z.string().optional(),
  intentLetter: z.string().optional(),
  applicationLetter: z.string().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
  cardCategory: z.string().optional(),
});
export const RewardFormRule = z.object({
  tenant: z.string().optional(),
  isPoints: z.boolean().optional(),
  isStamp: z.boolean().optional(),
  denominationPoints: z.number().optional(),
  points: z.number().optional(),
  denominationStamp: z.number().optional(),
  stamp: z.number().optional(),
  isActive: z.boolean().optional(),
});
export const RewardEventFormRule = z.object({
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  isPoints: z.boolean().optional(),
  isStamp: z.boolean().optional(),
  isLevel: z.boolean().optional(),
  denominationPoints: z.number().optional(),
  points: z.number().optional(),
  denominationStamp: z.number().optional(),
  stamp: z.number().optional(),
  startDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  endDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  level: z.string().optional(),
});
export const CampaignFormRule = z.object({
  tenant: z.string().optional(),
  level: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  image: z.string().optional(),
  points: z.number().optional(),
  stamp: z.number().optional(),
  redeemByPoints: z.boolean().optional(),
  redeemByStamp: z.boolean().optional(),
  multiRedeem: z.boolean().optional(),
  redeemLimit: z.number().optional(),
  startDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  endDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  constraint: z.string().optional(),
  limitPerMonth: z.number().optional(),
  forBirthday: z.boolean().optional(),
  birthdayWindowDays: z.number().optional(),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
});
export const RewardTypeFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  tenant: z.string().optional(),
});
export const RequestTypeFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});
export const TransactionFormRule = z.object({
  tenant: z.string().optional(),
  branch: z.string().optional(),
  userCode: z.string().optional(),
  cardSeries: z.string().optional(),
  reward: z.string().optional(),
  campaign: z.string().optional(),
  action: z.string().optional(),
  amount: z.number().optional(),
  points: z.number().optional(),
  stamp: z.number().optional(),
  remarks: z.string().optional(),
  branchName: z.string().optional(),
  customerName: z.string().optional(),
  campaignName: z.string().optional(),
  cardAlias: z.string().optional(),
  rewardEventName: z.string().optional(),
  voidedBy: z.string().optional(),
  voidedFirstName: z.string().optional(),
  voidedLastName: z.string().optional(),
  voidedAt: z.string().optional(),
  receiptNumber: z.string().optional(),
});
export const VoidTransactionFormRule = z.object({
  code: z.string().optional(),
  voidedBy: z.string().optional(),
  authentication: z.string().optional(),
});
export const BranchFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  head: z.string().optional(),
  tenant: z.string().optional(),
});
export const CardSeriesFormRule = z.object({
  tenant: z.string().optional(),
  cardCategory: z.string().optional(),
  quantity: z.number().optional(),
  cardCode: z.string().optional(),
  cardOwner: z.string().optional(),
  pin: z.number().optional(),
});
export const LevelFormRule = z.object({
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  avatar: z.string().optional(),
  points: z.number().optional(),
  stamp: z.number().optional(),
  constraint: z.string().optional(),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
  maxPoints: z.number().optional(),
  maxStamp: z.number().optional(),
  maxPointsPerDay: z.number().optional(),
  maxStampPerDay: z.number().optional(),
});
export const CardCategoryFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  tenant: z.string().optional(),
  expirationYears: z.number().optional(),
});
export const ReportFormRule = z.object({
  dateFilter: z.string().optional(),
  startDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  endDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
});
export const RequestFormRule = z.object({
  tenant: z.string().optional(),
  userCode: z.string().optional(),
  cardSeries: z.string().optional(),
  campaign: z.string().optional(),
  expiration: z.coerce.date().optional().nullish().or(z.string().max(0)),
  status: z.string().optional(),
  coupon: z.string().optional(),
});
export const RegistrationFormRule = z.object({
  tenant: z.string().optional(),
  branch: z.string().optional(),
  cardCategory: z.string().optional(),
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  email: z.string().optional(),
  mobileNumber: z.string().optional(),
  avatar: z.string().optional(),
  expiration: z.string().optional(),
  status: z.string().optional(),
  remarks: z.string().optional(),
  referralCode: z.string().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
});
export const IntializeUserFormRule = z.object({
  userCode: z.string().optional(),
  registrationDate: z.string().optional(),
});
export const TierLevelFormRule = z.object({  
  tenant: z.string().optional(),
  name: z.string().optional(),
  level: z.number().optional(),
  minAmount: z.number().optional(),
  maxAmount: z.number().optional(),
  description: z.string().optional(),
});
export const SplashScreenFormRule = z.object({  
  name: z.string().optional(),
  tenant: z.string().optional(),
  image: z.string().optional(),
  description: z.string().optional(),
  link: z.string().optional(),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
});
export const CouponFormRule = z.object({  
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  couponCode: z.string().min(5, "Minimum of 5 characters").optional(),
  validUntil: z.string().optional(),
  level: z.string().optional(),
  purpose: z.string().optional(),
  valueType: z.string().optional(),
  points: z.number().optional(),
  stamps: z.number().optional(),
  redeemLimit: z.number().optional(),
  isUserRedeemable: z.boolean().optional(),
});
export const ReferralSettingFormRule = z.object({  
  tenant: z.string().optional(),
  points: z.number().optional(),
  stamp: z.number().optional(),
  referralType: z.string().optional(),
});
export const ReferralFormRule = z.object({  
  tenant: z.string().optional(),
  referrer: z.string().optional(),
  referee: z.string().optional(),
  status: z.string().optional(),
});
export const ManualUpgradeFormRule = z.object({  
  code: z.string().optional(),
  tenant: z.string().optional(),
  branch: z.string().optional(),
  target_level: z.string().optional(),
});
export const BirthdayRewardFormRule = z.object({  
  tenant: z.string().optional(),
  points: z.number().optional(),
  stamp: z.number().optional(),
  expiryDays: z.number().optional(),
});
export const VehicleFormRule = z.object({  
  tenant: z.string().optional(),
  userCode: z.string().optional(),
  plateNumber: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  year: z.number().optional(),
  color: z.string().optional(),
  nickname: z.string().optional(),
  remarks: z.string().optional(),
  avatar: z.string().optional(),
  isBlocked: z.boolean().optional(),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
});
export const VehicleTransactionFormRule = z.object({  
  tenant: z.string().optional(),
  vehicle: z.string().optional(),
  transaction: z.string().optional(),
  userCode: z.string().optional(),
});
// RULE ADD
export const LoginRule = z.object({
  username: z.string().min(1, "Email/Username is required"),
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormSchema = z.infer<typeof LoginRule>;
export type TenantFormSchema = z.infer<typeof TenantFormRule>;
export type RoleFormSchema = z.infer<typeof RoleFormRule>;
export type PersonFormSchema = z.infer<typeof PersonFormRule>;
export type RewardFormSchema = z.infer<typeof RewardFormRule>;
export type RewardEventFormSchema = z.infer<typeof RewardEventFormRule>;
export type CampaignFormSchema = z.infer<typeof CampaignFormRule>;
export type RewardTypeFormSchema = z.infer<typeof RewardTypeFormRule>;
export type RequestTypeFormSchema = z.infer<typeof RequestTypeFormRule>;
export type TransactionFormSchema = z.infer<typeof TransactionFormRule>;
export type VoidTransactionFormSchema = z.infer<typeof VoidTransactionFormRule>;
export type BranchFormSchema = z.infer<typeof BranchFormRule>;
export type CardSeriesFormSchema = z.infer<typeof CardSeriesFormRule>;
export type LevelFormSchema = z.infer<typeof LevelFormRule>;
export type CardCategoryFormSchema = z.infer<typeof CardCategoryFormRule>;
export type ReportFormSchema = z.infer<typeof ReportFormRule>;
export type RequestFormSchema = z.infer<typeof RequestFormRule>;
export type RegistrationFormSchema = z.infer<typeof RegistrationFormRule>;
export type InitializeUserFormSchema = z.infer<typeof IntializeUserFormRule>;
export type TierLevelFormSchema = z.infer<typeof TierLevelFormRule>;
export type SplashScreenFormSchema = z.infer<typeof SplashScreenFormRule>;
export type CouponFormSchema = z.infer<typeof CouponFormRule>;
export type ReferralSettingFormSchema = z.infer<typeof ReferralSettingFormRule>;
export type ReferralFormSchema = z.infer<typeof ReferralFormRule>;
export type ManualUpgradeFormSchema = z.infer<typeof ManualUpgradeFormRule>;
export type BirthdayRewardFormSchema = z.infer<typeof BirthdayRewardFormRule>;
export type VehicleFormSchema = z.infer<typeof VehicleFormRule>;
export type VehicleTransactionFormSchema = z.infer<typeof VehicleTransactionFormRule>;
// SCHEMA ADD
